import { getTemplate, TEMPLATE_LIST } from './registry'
import { renderFullDocument, renderPreviewDocument } from './shared/deck-shell'
import type { RenderContext, SlideContent } from './types'

export { TEMPLATE_LIST, getTemplate } from './registry'
export type { TemplateDefinition, FieldSchema, FieldType, SlideContent, RenderContext } from './types'
export { cropToImgStyle, type CropAreaPercent } from './shared/crop'

export interface SlideInstance {
  templateId: string
  content: SlideContent
  ctx?: RenderContext
}

/** HTML de UM slide (`<section class="tpl-...">`), sem <head>/<html> em volta. */
export function renderSlideHtml(templateId: string, content: SlideContent, ctx: RenderContext = {}): string {
  const template = getTemplate(templateId)
  if (!template) return `<section class="slide"><p style="padding:24px;color:#b3261e">Template desconhecido: ${templateId}</p></section>`
  return template.render(content, ctx)
}

function dedupeCss(templateIds: string[]): string {
  const seen = new Set<string>()
  return templateIds
    .filter((id) => {
      if (seen.has(id)) return false
      seen.add(id)
      return true
    })
    .map((id) => getTemplate(id)?.css ?? '')
    .join('\n')
}

/** Documento completo pro iframe de preview de UM slide (editor). */
export function renderSlidePreviewDocument(instance: SlideInstance): string {
  const bodyHtml = renderSlideHtml(instance.templateId, instance.content, instance.ctx)
  const templateCss = dedupeCss([instance.templateId])
  return renderPreviewDocument({ bodyHtml, templateCss })
}

/** Documento completo da matéria inteira — "Copiar HTML" e captura via Playwright. */
export function renderArticleDocument(slides: SlideInstance[], opts: { title?: string; captureMode?: boolean; embedMode?: boolean } = {}): string {
  const bodyHtml = slides.map((s) => renderSlideHtml(s.templateId, s.content, s.ctx)).join('\n')
  const templateCss = dedupeCss(slides.map((s) => s.templateId))
  return renderFullDocument({ bodyHtml, templateCss, title: opts.title, captureMode: opts.captureMode, embedMode: opts.embedMode, slideCount: slides.length })
}

function escapeAttribute(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * Snippet pronto para colar no bloco "HTML personalizado" do WordPress.
 * O conteúdo completo da matéria vai dentro de `srcdoc`, isolado em iframe,
 * para não herdar nem quebrar CSS/JS do tema/editor do WordPress.
 */
export function renderArticleIframeEmbed(slides: SlideInstance[], opts: { title?: string } = {}): string {
  const title = opts.title || 'Lorena Magazine'
  const html = renderArticleDocument(slides, { title })
  const slideCount = Math.max(1, slides.length)
  const iframeTitle = escapeAttribute(title)
  const srcdoc = escapeAttribute(html)

  return `<div class="lorena-magazine-embed" style="width:100%;max-width:640px;margin:0 auto;aspect-ratio:640/${800 * slideCount};">
  <iframe title="${iframeTitle}" srcdoc="${srcdoc}" loading="lazy" scrolling="no" style="display:block;width:100%;height:100%;border:0;overflow:hidden;background:#fff;"></iframe>
</div>`
}

export function renderArticleIframeUrlEmbed(src: string, opts: { title?: string; slideCount?: number } = {}): string {
  const title = opts.title || 'Lorena Magazine'
  const slideCount = Math.max(1, opts.slideCount || 1)
  const height = 800 * slideCount
  const desktopScale = 1.2
  const desktopWidth = Math.round(640 * desktopScale)
  const desktopHeight = Math.round(height * desktopScale)
  const iframeTitle = escapeAttribute(title)
  const iframeSrc = escapeAttribute(src)

  return `<div class="lorena-magazine-embed" style="width:min(100vw,${desktopWidth}px);max-width:${desktopWidth}px;margin-left:50%;transform:translateX(-50%);line-height:0;background:#fff;">
  <iframe title="${iframeTitle}" src="${iframeSrc}" width="${desktopWidth}" height="${desktopHeight}" loading="lazy" scrolling="no" style="display:block;width:100%!important;max-width:${desktopWidth}px!important;height:auto!important;aspect-ratio:${desktopWidth}/${desktopHeight}!important;border:0!important;overflow:hidden!important;background:#fff!important;"></iframe>
</div>`
}

export const ALL_TEMPLATE_IDS = TEMPLATE_LIST.map((t) => t.id)
